import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import axiosInstance from './api';
import { paths } from './paths';
import { store } from '../store/index';
import { setLoader } from '../store/builder/reducer';

// Available translations - will be populated from backend
const resources = {};

// Load translations from backend
export const loadTranslations = async (language = 'en') => {
  try {
    // Check if translations are already loaded for this language
    // This ensures we only make one API request per language
    if (resources[language]) {
      // Ensure translations are in i18n if it's already initialized
      // This handles cases where translations were cached but i18n wasn't ready yet
      if (i18n.isInitialized && typeof i18n.addResourceBundle === 'function') {
        // addResourceBundle with merge=true safely updates existing resources
        i18n.addResourceBundle(language, 'translation', resources[language].translation, true, true);
      }
      return resources[language].translation;
    }

    localStorage.setItem('language', language);
    // Create a custom request - language is sent via Accept-Language header
    store.dispatch(setLoader(true));
    const response = await axiosInstance.get(paths.languageJson);

    let translations = response.data;

    // Validate translations - ensure it's an object and not just a key
    if (!translations || typeof translations !== 'object' || Array.isArray(translations)) {
      console.warn('Invalid translations format received from backend, using empty object');
      translations = {};
    }

    // Filter out any keys that have invalid values (in case backend returns malformed data)
    const validTranslations = {};
    for (const key in translations) {
      if (translations.hasOwnProperty(key)) {
        const value = translations[key];
        // Only include if value is a string, number, boolean, or nested object
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || (typeof value === 'object' && value !== null && !Array.isArray(value))) {
          validTranslations[key] = value;
        } else {
          console.warn(`Skipping invalid translation key: ${key} with value:`, value);
        }
      }
    }

    // Update resources with translations
    resources[language] = {
      translation: validTranslations
    };

    // Add the resource to i18n only if it's already initialized
    if (i18n.isInitialized && typeof i18n.addResourceBundle === 'function') {
      i18n.addResourceBundle(language, 'translation', validTranslations, true, true);
      // Emit a custom event to notify components that translations have been loaded
      // Use languageChanged event as a workaround if emit doesn't exist
      if (typeof i18n.emit === 'function') {
        i18n.emit('loaded', language);
      } else {
        // Trigger languageChanged to force components to re-render
        i18n.emit('languageChanged', language);
      }
    }

    store.dispatch(setLoader(false));
    return validTranslations;
  } catch (error) {
    store.dispatch(setLoader(false));
    console.error('Failed to load translations for', language, 'from backend:', error);
    // Return empty object as fallback
    return {};
  }
};

// Change language
export const changeLanguage = async (language) => {
  try {

    // Load translations for the requested language
    if (!resources[language]) {
      await loadTranslations(language);
    }

    // Save language preference to localStorage
    localStorage.setItem('language', language);

    // Change the i18n language
    await i18n.changeLanguage(language);
  } catch (error) {
    console.error('Failed to change language:', error);
  }
};

// Get current language
export const getCurrentLanguage = () => {
  return i18n.language || localStorage.getItem('language') || 'en';
};

// Get available languages
export const getAvailableLanguages = () => {
  // Return common languages, backend will determine actual availability
  return ['en', 'fr'];
};

// Clear saved language preference
export const clearLanguagePreference = () => {
  localStorage.removeItem('language');
};

// Promise that resolves when i18n is fully initialized
let i18nReadyPromise = null;
let i18nReadyResolve = null;

// Create promise for i18n initialization
const createI18nReadyPromise = () => {
  if (!i18nReadyPromise) {
    i18nReadyPromise = new Promise((resolve) => {
      i18nReadyResolve = resolve;
    });
  }
  return i18nReadyPromise;
};

// Get or create the i18n ready promise
export const getI18nReadyPromise = () => {
  if (i18n.isInitialized) {
    return Promise.resolve();
  }
  return createI18nReadyPromise();
};

// Get saved language preference synchronously for init
const savedLanguage = localStorage.getItem('language') || 'en';

// Initialize i18n synchronously so useTranslation() is always available.
// Translations are loaded asynchronously and added via addResourceBundle.
i18n
  .use(initReactI18next)
  .init({
    resources: {},
    lng: savedLanguage,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    returnNull: false,
    returnEmptyString: false,
    returnObjects: true,
  });

// Load translations asynchronously after i18n is ready
const initializeTranslations = async () => {
  try {
    const translations = await loadTranslations(savedLanguage);
    if (translations && Object.keys(translations).length > 0) {
      i18n.addResourceBundle(savedLanguage, 'translation', translations, true, true);
      await i18n.changeLanguage(savedLanguage);
    }
  } catch (error) {
    console.error('Failed to load initial translations:', error);
  } finally {
    if (i18nReadyResolve) {
      i18nReadyResolve();
      i18nReadyResolve = null;
    }
  }
};

initializeTranslations();

export default i18n;
